import { BlockId } from '@shared/block-ids';

export interface BlockDef {
  id: BlockId;
  name: string;
  solid: boolean;
  transparent: boolean;
  cutout: boolean; // alpha-tested (leaves, flowers) vs truly translucent (glass, water)
  liquid: boolean;
  lightEmission: number;
  hardness: number;
  toolType?: 'pickaxe' | 'axe' | 'shovel';
  textures: {
    top: number;
    bottom: number;
    front: number;
    back: number;
    left: number;
    right: number;
  };
  drops?: BlockId;
}

export enum ToolType {
  NONE = 0,
  WOOD_PICKAXE = 100,
  WOOD_AXE = 101,
  WOOD_SHOVEL = 102,
  WOOD_SWORD = 103,
  STONE_PICKAXE = 104,
  STONE_AXE = 105,
  STONE_SHOVEL = 106,
  STONE_SWORD = 107,
  IRON_PICKAXE = 108,
  IRON_AXE = 109,
  IRON_SHOVEL = 110,
  IRON_SWORD = 111,
  DIAMOND_PICKAXE = 112,
  DIAMOND_AXE = 113,
  DIAMOND_SHOVEL = 114,
  DIAMOND_SWORD = 115,
}

export interface ToolDef {
  id: ToolType;
  name: string;
  type: 'pickaxe' | 'axe' | 'shovel' | 'sword';
  tier: 'wood' | 'stone' | 'iron' | 'diamond';
  miningSpeed: number; // multiplier
  durability: number;
  attackDamage: number;
}

export interface ItemStack {
  id: BlockId;
  count: number;
  toolId?: ToolType;
  durability?: number;
}

export interface AABB {
  minX: number;
  minY: number;
  minZ: number;
  maxX: number;
  maxY: number;
  maxZ: number;
}

export interface RaycastHit {
  x: number;
  y: number;
  z: number;
  face: number;
  normal: [number, number, number];
  distance: number;
}

export enum GameScreen {
  MAIN_MENU = 'main_menu',
  WORLD_SELECT = 'world_select',
  WORLD_CREATE = 'world_create',
  SERVER_BROWSER = 'server_browser',
  SERVER_CREATE = 'server_create',
  SKIN_SELECT = 'skin_select',
  SETTINGS = 'settings',
  LOADING = 'loading',
  PLAYING = 'playing',
  PAUSED = 'paused',
  INVENTORY = 'inventory',
  CHAT = 'chat',
}

export interface GameSettings {
  renderDistance: number;
  fov: number;
  mouseSensitivity: number;
  masterVolume: number;
  musicVolume: number;
  sfxVolume: number;
  showFps: boolean;
  shadows: boolean;
  smoothLighting: boolean;
  clouds: boolean;
  particles: boolean;
  fullscreen: boolean;
  vsync: boolean;
  guiScale: number;
  keyBindings: Record<string, string>;
}

export const DEFAULT_SETTINGS: GameSettings = {
  renderDistance: 6,
  fov: 70,
  mouseSensitivity: 0.002,
  masterVolume: 1.0,
  musicVolume: 0.5,
  sfxVolume: 1.0,
  showFps: false,
  shadows: true,
  smoothLighting: true,
  clouds: true,
  particles: true,
  fullscreen: false,
  vsync: true,
  guiScale: 2,
  keyBindings: {
    forward: 'KeyW',
    backward: 'KeyS',
    left: 'KeyA',
    right: 'KeyD',
    jump: 'Space',
    sprint: 'ShiftLeft',
    sneak: 'ControlLeft',
    inventory: 'KeyE',
    chat: 'KeyT',
    drop: 'KeyQ',
    debug: 'F3',
    fullscreen: 'F11',
    pause: 'Escape',
  },
};

export interface WorldInfo {
  id: string;
  name: string;
  seed: number;
  createdAt: number;
  lastPlayed: number;
  gameMode: 'survival' | 'creative';
}

export interface ServerInfo {
  id: string;
  name: string;
  address: string;
  maxPlayers: number;
  currentPlayers: number;
  motd: string;
  ping: number;
}
